import React, { useMemo, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useFarms, useAnimals } from "@/lib/repo";
import { applyBulkDeleteLocal } from "@/lib/repo";
import * as api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useOnline } from "@/lib/useOnline";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AnimalListRow } from "@/components/AnimalListRow";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Search, Baby, PencilLine, ChevronDown, Zap, Boxes, ClipboardList, HeartPulse, CheckSquare, X, Trash2, Star, Heart } from "lucide-react";
import { toast } from "sonner";

export default function Animals() {
  const { user } = useAuth();
  const online = useOnline();
  const [searchParams, setSearchParams] = useSearchParams();
  const farms = useFarms();
  const [filters, setFilters] = useState({ q: "", farm_id: "", category: "", status: "active", needs_detail: searchParams.get("pendientes") === "1", is_sick: searchParams.get("salud") === "1", is_elite: searchParams.get("elite") === "1", is_good_mother: searchParams.get("madres") === "1" });
  const loading = false;

  // Selección múltiple + borrado masivo (Tanda 2 — Papelera). Habilitado
  // recién ahora que la papelera funciona, por pedido explícito del cliente.
  // Requiere conexión: el freno de seguridad coordina con el servidor
  // (backup previo, conteo de borrados recientes por dueño).
  const canDelete = user?.role === "owner";
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmBasic, setConfirmBasic] = useState(false);
  const [thresholdInfo, setThresholdInfo] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const queryFilters = useMemo(() => {
    const params = { assigned_farm_ids: (user?.role === "handler" || user?.role === "vet") ? (user?.assigned_farm_ids || []) : undefined };
    if (filters.q) params.q = filters.q;
    if (filters.farm_id && filters.farm_id !== "all") params.farm_id = filters.farm_id;
    if (filters.category && filters.category !== "all") params.category = filters.category;
    if (filters.is_sick) params.is_sick = true;
    else if (filters.is_elite) params.is_elite = true; // Fix P
    else if (filters.is_good_mother) params.is_good_mother = true; // Fix Q
    else if (filters.needs_detail) params.needs_detail = true;
    else if (filters.status && filters.status !== "all") params.status = filters.status;
    return params;
  }, [filters, user]);
  const animals = useAnimals(queryFilters);

  const togglePending = () => {
    const next = !filters.needs_detail;
    setFilters((f) => ({ ...f, needs_detail: next, is_sick: false, is_elite: false, is_good_mother: false }));
    setSearchParams(next ? { pendientes: "1" } : {});
  };

  const toggleSick = () => {
    const next = !filters.is_sick;
    setFilters((f) => ({ ...f, is_sick: next, needs_detail: false, is_elite: false, is_good_mother: false }));
    setSearchParams(next ? { salud: "1" } : {});
  };

  // Fix P (2026-10-06): apartado "Élite", igual que "Con problemas de salud".
  const toggleElite = () => {
    const next = !filters.is_elite;
    setFilters((f) => ({ ...f, is_elite: next, is_sick: false, needs_detail: false, is_good_mother: false }));
    setSearchParams(next ? { elite: "1" } : {});
  };

  // Fix Q (2026-10-21): apartado "Buenas madres".
  const toggleGoodMother = () => {
    const next = !filters.is_good_mother;
    setFilters((f) => ({ ...f, is_good_mother: next, is_elite: false, is_sick: false, needs_detail: false }));
    setSearchParams(next ? { madres: "1" } : {});
  };

  const filtered = useMemo(() => animals, [animals]);

  // Fix B (2026-08-24): con 3.091 animales activos, dibujar todas las filas de una
  // dejaba el teléfono lento. Se renderizan de a tandas y se agregan más al pedirlo.
  // El filtrado NO cambia: 'filtered' sigue siendo la lista completa y el contador
  // sigue mostrando el total.
  const PAGE_SIZE = 100;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [JSON.stringify(queryFilters)]);
  const visible = useMemo(() => (filtered || []).slice(0, visibleCount), [filtered, visibleCount]);

  const toggleSelectMode = () => {
    if (!online) { toast.error("Necesitás conexión para la selección múltiple (por el freno de seguridad y el backup previo)."); return; }
    setSelectMode((v) => !v);
    setSelectedIds(new Set());
  };

  const toggleSelectId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const runBulkDelete = async (confirm) => {
    setDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const r = await api.bulkDeleteAnimals(ids, confirm);
      if (r.status === "confirm_required") {
        setConfirmBasic(false);
        setThresholdInfo(r);
        return;
      }
      await applyBulkDeleteLocal(r.deleted_ids);
      toast.success(`${r.deleted} animal(es) eliminado(s) — movidos a la papelera${r.backup_created ? " · se generó un backup de seguridad" : ""}`);
      setSelectMode(false);
      setSelectedIds(new Set());
      setConfirmBasic(false);
      setThresholdInfo(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pude eliminar la selección");
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-muted-foreground">Inventario maestro</div>
          <h1 className="font-editorial text-[32px] sm:text-[40px] leading-[1.05] tracking-tight mt-0.5">Animales</h1>
          <p className="text-[13px] text-muted-foreground mt-1">
            {loading ? "Cargando…" : <><span className="font-mono tabular-nums">{animals.length}</span> {animals.length === 1 ? "animal" : "animales"} encontrados</>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canDelete && (
            <Button
              variant={selectMode ? "secondary" : "outline"}
              size="sm"
              onClick={toggleSelectMode}
              data-testid="animals-select-mode-toggle"
              title={!online ? "Necesitás internet para la selección múltiple" : undefined}
            >
              {selectMode ? <X size={15} /> : <CheckSquare size={15} />}
              {selectMode ? "Cancelar" : "Seleccionar"}
            </Button>
          )}
          {!selectMode && user?.role !== "vet" && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="animals-new-button"><Plus size={16}/>Nuevo<ChevronDown size={14} className="opacity-70" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuItem asChild data-testid="animals-new-initial">
                  <Link to="/inventario-inicial"><Boxes size={15} className="mr-2 text-primary" />Crear inventario inicial</Link>
                </DropdownMenuItem>
                {user?.role !== "handler" && (
                  <DropdownMenuItem asChild data-testid="animals-new-actions">
                    <Link to="/acciones"><Zap size={15} className="mr-2 text-primary" />Todas las acciones</Link>
                  </DropdownMenuItem>
                )}
                {user?.role !== "handler" && (
                  <DropdownMenuItem asChild data-testid="animals-new-birth">
                    <Link to="/animales/nacimiento"><Baby size={15} className="mr-2 text-primary" />Registro de nacimiento</Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild data-testid="animals-new-manual">
                  <Link to="/animales/nuevo"><PencilLine size={15} className="mr-2" />Carga manual</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Pendientes de completar (carga inicial) */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={togglePending}
          data-testid="animals-pending-toggle"
          className={cn(
            "inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium border transition-colors ring-focus",
            filters.needs_detail
              ? "bg-[hsl(var(--c-evento)/0.16)] border-[hsl(var(--c-evento)/0.5)] text-foreground"
              : "bg-card border-border text-muted-foreground hover:bg-muted"
          )}
        >
          <ClipboardList size={14} />
          Pendientes de completar
        </button>
        <button
          onClick={toggleSick}
          data-testid="animals-health-toggle"
          className={cn(
            "inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium border transition-colors ring-focus",
            filters.is_sick
              ? "bg-[hsl(var(--brand-ochre)/0.16)] border-[hsl(var(--brand-ochre)/0.5)] text-foreground"
              : "bg-card border-border text-muted-foreground hover:bg-muted"
          )}
        >
          <HeartPulse size={14} />
          Con problemas de salud
        </button>
        <button
          onClick={toggleElite}
          data-testid="animals-elite-toggle"
          className={cn(
            "inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium border transition-colors ring-focus",
            filters.is_elite
              ? "bg-[hsl(var(--brand-warm)/0.16)] border-[hsl(var(--brand-warm)/0.5)] text-foreground"
              : "bg-card border-border text-muted-foreground hover:bg-muted"
          )}
        >
          <Star size={14} />
          Élite
        </button>
        <button
          onClick={toggleGoodMother}
          data-testid="animals-good-mother-toggle"
          className={cn(
            "inline-flex items-center gap-2 px-3.5 h-9 rounded-full text-[12.5px] font-medium border transition-colors ring-focus",
            filters.is_good_mother
              ? "bg-[hsl(var(--state-present-bg))] border-[hsl(var(--state-present)/0.5)] text-foreground"
              : "bg-card border-border text-muted-foreground hover:bg-muted"
          )}
        >
          <Heart size={14} />
          Buenas madres
        </button>
      </div>

      {/* Filters card */}
      <div className="rounded-[var(--radius)] border border-border bg-card p-3 sm:p-4 grid grid-cols-1 sm:grid-cols-4 gap-2 shadow-paper">
        <div className="sm:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="animals-search-input"
            value={filters.q}
            onChange={(e)=>setFilters({ ...filters, q: e.target.value })}
            placeholder="Buscar por chip / lomo / tatuaje…"
            className="h-11 sm:h-10 pl-9"
          />
        </div>
        <Select value={filters.farm_id || "all"} onValueChange={(v)=>setFilters({ ...filters, farm_id: v === "all" ? "" : v })}>
          <SelectTrigger className="h-10" data-testid="animals-farm-filter"><SelectValue placeholder="Todas las fincas" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las fincas</SelectItem>
            {farms.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={(filters.needs_detail || filters.is_sick || filters.is_elite || filters.is_good_mother) ? "all" : (filters.status || "all")} disabled={filters.needs_detail || filters.is_sick || filters.is_elite || filters.is_good_mother} onValueChange={(v)=>setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="h-10" data-testid="animals-status-filter"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="active">Activo</SelectItem>
            <SelectItem value="sold">Vendido</SelectItem>
            <SelectItem value="slaughtered">Sacrificado</SelectItem>
            <SelectItem value="dead">Muerto</SelectItem>
            <SelectItem value="lost">Extraviado</SelectItem>
            <SelectItem value="in_transit">En tránsito</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div
        className="rounded-[var(--radius)] border border-border bg-card shadow-paper overflow-hidden"
        data-testid="animals-list"
      >
        {loading && (
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-[calc(var(--radius)-0.35rem)]" />)}
          </div>
        )}
        {!loading && filtered.length === 0 && (
          <div data-testid="empty-animals" className="p-10 text-center">
            <h3 className="font-editorial text-[24px] tracking-tight mb-1">Todavía no hay animales</h3>
            <p className="text-sm text-muted-foreground mb-5 max-w-sm mx-auto">
              Sumá el primero escaneando un chip o cargándolo a mano. Podés importar lotes por CSV más adelante.
            </p>
            <Button asChild data-testid="empty-animals-cta">
              <Link to="/animales/nuevo"><Plus size={16}/>Nuevo animal</Link>
            </Button>
          </div>
        )}
        {/* Fix B (2026-08-24): se dibuja solo la tanda visible; el resto se agrega
            al tocar "Ver más". Mismos animales, mismo orden, mismo filtrado. */}
        {!loading && visible.map((a) => (
          <AnimalListRow
            key={a.id}
            animal={a}
            selectMode={selectMode}
            selected={selectedIds.has(a.id)}
            onToggleSelect={toggleSelectId}
          />
        ))}
        {!loading && filtered.length > visible.length && (
          <Button
            variant="outline"
            className="h-11 w-full mt-1"
            data-testid="animals-load-more"
            onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
          >
            Ver más ({filtered.length - visible.length} restantes)
          </Button>
        )}
      </div>

      {/* Barra de acción flotante — selección múltiple */}
      {selectMode && selectedIds.size > 0 && (
        <div
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40 bg-card border border-border shadow-paper rounded-full pl-4 pr-2 py-2 flex items-center gap-3"
          data-testid="animals-selection-bar"
        >
          <span className="text-[13px] font-medium tabular-nums">{selectedIds.size} seleccionado{selectedIds.size === 1 ? "" : "s"}</span>
          <Button
            variant="destructive" size="sm"
            onClick={() => setConfirmBasic(true)}
            data-testid="animals-delete-selected-button"
          >
            <Trash2 size={14} className="mr-1.5" />
            Eliminar
          </Button>
        </div>
      )}

      {/* Confirmación básica (siempre se pide, sin importar la cantidad) */}
      <AlertDialog open={confirmBasic} onOpenChange={setConfirmBasic}>
        <AlertDialogContent data-testid="animals-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar {selectedIds.size} animal(es)?</AlertDialogTitle>
            <AlertDialogDescription>
              Van a moverse a la Papelera (Ajustes → Papelera), donde podés restaurarlos durante 30
              días. Después de eso se borran para siempre.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="animals-delete-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runBulkDelete(false)}
              disabled={deleting}
              data-testid="animals-delete-confirm-accept"
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? "Eliminando…" : "Sí, eliminar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Freno de seguridad por CANTIDAD/FRECUENCIA (>10 en 10 min) */}
      <AlertDialog open={!!thresholdInfo} onOpenChange={(o) => !o && setThresholdInfo(null)}>
        <AlertDialogContent data-testid="animals-threshold-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Borrado masivo detectado</AlertDialogTitle>
            <AlertDialogDescription>
              {thresholdInfo?.message}
              {" "}Se va a generar un backup completo de seguridad antes de continuar, por si necesitás deshacerlo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="animals-threshold-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => runBulkDelete(true)}
              disabled={deleting}
              data-testid="animals-threshold-confirm-accept"
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? "Eliminando…" : "Sí, entiendo, eliminar igual"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
